import {useCallback} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';

import {
  Box,
  Checkbox,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {useCurrency} from '@salesforce/retail-react-app/app/hooks';

import {formatCurrencyHideTrailingZeroCents} from '@amblique/king-living/app/utils/format-currency-hide-zero-cents';
import {sfccProductIdDataAttrs} from '@amblique/king-living/app/utils/product-utils';

function handleOptionalAddOnCheckboxKeyDown(event, isDisabled, onToggle) {
  if (isDisabled) return;

  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    event.stopPropagation();
    onToggle();
  }
}

/**
 * Single-product optional add-on item (content section only).
 * Header is rendered by parent OptionalAddOnGroup.
 *
 * Displays:
 * ┌──────────────────────────────────────────────────────┐
 * │ product name               price          [checkbox] │
 * └──────────────────────────────────────────────────────┘
 *
 * Note: No "See details" button for single layout (info already in header).
 *
 * @param {Object} props
 * @param {string} props.productId
 * @param {string} props.name
 * @param {number|null} [props.price]
 * @param {boolean} props.isChecked
 * @param {boolean} [props.isDisabled]
 * @param {Function} props.onChange - (productId, checked: boolean)
 * @param {Function} [props.onDetailsClick] - (Optional, not used for single layout)
 */
const OptionalAddOnSingle = ({
  productId,
  name,
  price,
  isChecked,
  isDisabled = false,
  onChange,
  onDetailsClick,
}) => {
  const intl = useIntl();
  const styles = useMultiStyleConfig('OptionalAddOns', {});
  const {currency} = useCurrency();

  const handleChange = useCallback((e) => {
    onChange(productId, e.target.checked);
  }, [onChange, productId]);

  const handleToggle = useCallback(() => {
    onChange(productId, !isChecked);
  }, [isChecked, onChange, productId]);

  const handleDetailsClick = useCallback(() => {
    onDetailsClick?.(productId);
  }, [onDetailsClick, productId]);

  const priceLabel = price != null ?
    formatCurrencyHideTrailingZeroCents(intl, price, currency) :
    null;

  const checkboxId = `optional-add-on-single-${productId}`;

  return (
    <Box className="optional-add-on-single__root" {...sfccProductIdDataAttrs('option', productId)}>
      <Box className="optional-add-on-single__content" {...styles.item}>
        <Box className="optional-add-on-single__meta" {...styles.itemMeta}>
          <Text
            as="label"
            className="optional-add-on-single__label"
            htmlFor={checkboxId}
            {...styles.itemLabel}
          >
            {name}
          </Text>
          {onDetailsClick && (
            <Box
              as="button"
              type="button"
              {...styles.detailsLink}
              onClick={handleDetailsClick}
            >
              {intl.formatMessage({
                defaultMessage: 'See details',
                id: 'optional_add_ons.see_details',
              })}
            </Box>
          )}
        </Box>

        {priceLabel && (
          <Text className="optional-add-on-single__price" {...styles.itemPrice}>
            +{priceLabel}
          </Text>
        )}

        <Checkbox
          className="optional-add-on-single__checkbox"
          id={checkboxId}
          isChecked={isChecked}
          isDisabled={isDisabled}
          onChange={handleChange}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => handleOptionalAddOnCheckboxKeyDown(event, isDisabled, handleToggle)}
          {...styles.itemCheckbox}
        />
      </Box>
    </Box>
  );
};

OptionalAddOnSingle.displayName = 'OptionalAddOnSingle';

OptionalAddOnSingle.propTypes = {
  productId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  price: PropTypes.number,
  isChecked: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onDetailsClick: PropTypes.func,
};

export default OptionalAddOnSingle;
